import { create } from 'zustand';
import {
  IntegrationRule,
  WebConnectorList,
  CreateWebConnectorParams,
  UpdateWebConnectorParams,
  getWebConnectors,
  createWebConnector,
  updateWebConnector,
  deleteWebConnector,
  refreshWebConnector,
} from '@mygpt/app';

const DEFAULT_PAGE_SIZE = 10;

interface WebConnectorState {
  // Web Connector 列表数据
  connectors: WebConnectorList;
  loading: boolean;
  error: string | null;

  // 排序状态
  sortBy: string;
  reverseSortDirection: boolean;

  // 当前选中的 connector
  selectedConnector: IntegrationRule | null;

  // Actions
  fetchConnectors: (params: { datasetId: string; page?: number; size?: number; order_by?: string }) => Promise<void>;
  createConnector: (datasetId: string, params: CreateWebConnectorParams) => Promise<void>;
  updateConnector: (datasetId: string, connectorId: string, params: UpdateWebConnectorParams) => Promise<void>;
  deleteConnector: (datasetId: string, connectorId: string) => Promise<void>;
  refreshConnector: (datasetId: string, connectorId: string) => Promise<void>;

  selectConnector: (connector: IntegrationRule | null) => void;

  // 排序方法
  setSorting: (field: string) => void;
}

const initialConnectorList: WebConnectorList = {
  items: [],
  page: 1,
  pages: 0,
  size: DEFAULT_PAGE_SIZE,
  total: 0,
};

export const useWebConnectorStore = create<WebConnectorState>((set, get) => ({
  // Initial state
  connectors: initialConnectorList,
  loading: false,
  error: null,
  sortBy: '',
  reverseSortDirection: false,
  selectedConnector: null,

  // Actions
  fetchConnectors: async ({
    datasetId,
    page = 1,
    size = DEFAULT_PAGE_SIZE,
    order_by = '',
  }: {
    datasetId: string;
    page?: number;
    size?: number;
    order_by?: string;
  }) => {
    if (!datasetId) {
      console.error('datasetId is undefined');
      return;
    }

    const { loading } = get();
    if (loading) return;

    try {
      set({ loading: true, error: null });
      const response = await getWebConnectors(datasetId, {
        page,
        size,
        order_by,
      });
      set({ connectors: response.data, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  createConnector: async (datasetId: string, params: CreateWebConnectorParams) => {
    try {
      set({ loading: true, error: null });
      const response = await createWebConnector(datasetId, params);
      const { connectors } = get();

      // 添加新的 connector 到列表
      set({
        connectors: {
          ...connectors,
          items: [response.data, ...connectors.items],
          total: connectors.total + 1,
        },
        loading: false,
      });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  updateConnector: async (datasetId: string, connectorId: string, params: UpdateWebConnectorParams) => {
    try {
      set({ loading: true, error: null });
      const response = await updateWebConnector(datasetId, connectorId, params);
      const { connectors, selectedConnector } = get();

      // 更新列表中的 connector
      const updatedItems = connectors.items.map((item) => (item.id === connectorId ? response.data : item));

      set({
        connectors: { ...connectors, items: updatedItems },
        selectedConnector: selectedConnector?.id === connectorId ? response.data : selectedConnector,
        loading: false,
      });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  deleteConnector: async (datasetId: string, connectorId: string) => {
    try {
      set({ loading: true, error: null });
      await deleteWebConnector(datasetId, connectorId);
      const { connectors, selectedConnector } = get();

      // 从列表中移除 connector
      const filteredItems = connectors.items.filter((item) => item.id !== connectorId);

      set({
        connectors: {
          ...connectors,
          items: filteredItems,
          total: connectors.total - 1,
        },
        selectedConnector: selectedConnector?.id === connectorId ? null : selectedConnector,
        loading: false,
      });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  refreshConnector: async (datasetId: string, connectorId: string) => {
    set({ error: null });
    await refreshWebConnector(datasetId, connectorId);
    // 刷新连接器列表以获取最新状态
    const { connectors, sortBy, reverseSortDirection } = get();
    await get().fetchConnectors({
      datasetId,
      page: connectors.page,
      size: connectors.size,
      order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
    });
  },

  selectConnector: (connector: IntegrationRule | null) => {
    set({ selectedConnector: connector });
  },

  setSorting: (field: string) => {
    const { sortBy, reverseSortDirection } = get();
    const reversed = field === sortBy ? !reverseSortDirection : false;
    set({
      sortBy: field,
      reverseSortDirection: reversed,
    });
  },
}));
